import { sha3_256_hash_string } from "../cryptoUtils";
import { notifications } from "@mantine/notifications";
import { parseMetadata } from "./parseMetadata";
import { stringToTimestamp } from "./stringToTimestamp";

/** Required column names for legacy vbase_metadata CSV (matches parseMetadata). */
const META_REQUIRED_HEADERS = ["collection_name", "user_address"];
const VERIFY_DATA_HEADERS = ["t", "c", "f"];

/**
 * Convert CSV data to JSON format
 */
const csvToJson = (csvData) => {
  const lines = csvData.split("\n");
  const headers = lines[0].split(",");
  const json = lines.slice(1).reduce((acc, line) => {
    if (line.trim() === "") return acc; // if empty lines skip
    const values = line.split(",");
    const obj = headers.reduce((headerAcc, header, idx) => {
      headerAcc[header.trim()] = values[idx]?.trim(); // Map object keys to values
      return headerAcc;
    }, {});
    acc.push(obj);
    return acc;
  }, []);
  return json;
};

/**
 * Convert JSON data to CSV format
 * @param {*} jsonData
 * @param {*} headers
 * @returns
 */
const jsonToCsv = (jsonData, headers) => {
  let csv = headers.join(",") + "\n";
  jsonData.forEach((row) => {
    csv += headers.map((header) => row[header]).join(",") + "\n";
  });
  return csv;
};

/**
 * Group data by a specific column
 * @param {*} data
 * @param {*} column
 * @returns
 */
const groupBy = (data, column) => {
  const uniqueKeys = Array.from(new Set(data.map((row) => row[column])));
  return uniqueKeys.map((key) => ({
    [column]: key,
    group: data.filter((row) => row[column] === key),
  }));
};

/**
 * Remove a specific column from data
 * @param {*} data
 * @param {*} column
 * @returns
 */
/* eslint-disable no-unused-vars */
const removeColumn = (data, column) => {
  return data.map((row) => {
    const { [column]: _, ...rest } = row; // Exclude the specified column
    return rest;
  });
};

const tryParseStringToTimestamp = (extractedDatetime, collectionTimezone) => {
  try {
    const date = stringToTimestamp(extractedDatetime, collectionTimezone);
    return date.toUTC().toFormat("yyyy-MM-dd HH:mm:ss+00:00");
  } catch (error) {
    console.log(error);
    notifications.show({
      title: "Error",
      message: error.message,
      color: "red",
    });
    return null;
  }
};

/**
 * Process CSV data
 * @param {*} dataLines
 * @returns
 */
const processCsvData = (dataLines, collectionTimezone) => {
  const jsonData = csvToJson(dataLines);

  return jsonData.map((row) => {
    row.t = tryParseStringToTimestamp(row.t, collectionTimezone);
    return row;
  });
};

const parseVerifyHeader = (line) =>
  (line || "")
    .split(",")
    .map((h) => h.trim())
    .filter(Boolean);

const isMetaHeader = (line) => {
  const headers = parseVerifyHeader(line);
  if (headers.length === 0) return false;
  return META_REQUIRED_HEADERS.every((name) => headers.includes(name));
};

const isAlreadyVerifyCsv = (csvData) => {
  const lines = (csvData || "").trim().split("\n").filter(Boolean);
  if (lines.length === 0) return false;
  if (isMetaHeader(lines[0])) return false;
  const headers = parseVerifyHeader(lines[0]);
  if (!headers.includes("t") || !headers.includes("c")) return false;
  // If the CSV contains only verify headers (t,c,f in any order), treat as already-final.
  const headerSet = new Set(headers);
  for (const h of headerSet) {
    if (!VERIFY_DATA_HEADERS.includes(h)) return false;
  }
  return true;
};

const processCSVWithHash = async (csvData, onErrorMessage) => {
  let defaultTimezone = "";
  const original = csvData ?? "";
  const trimmed = original.trim();
  if (!trimmed) {
    notifications.show({
      title: "Error",
      message: "CSV file is empty.",
      color: "red",
    });
    onErrorMessage("CSV file is empty.");
    return null;
  }

  // If the user already provided a verify-ready CSV (t,c[,f]) without metadata,
  // don't re-hash or rewrite it; pass through original bytes. Detection uses `trimmed`
  // only; we only ensure a single trailing newline when missing.
  if (isAlreadyVerifyCsv(trimmed)) {
    return original.endsWith("\n") ? original : `${original}\n`;
  }

  const lines = trimmed.split("\n");

  const hasMeta = isMetaHeader(lines[0]);

  let collectionName = "";
  let userAddress = "";
  let collectionTimezone = defaultTimezone;

  let dataLines = trimmed;
  if (hasMeta) {
    // Best-effort parse. If metadata is malformed, we still try to process as data-only.
    const parsed = parseMetadata(lines, defaultTimezone);
    if (parsed) {
      collectionName = parsed.collectionName || "";
      userAddress = parsed.userAddress || "";
      collectionTimezone = parsed.collectionTimezone || defaultTimezone;
      dataLines = lines.slice(2).join("\n");
    } else {
      dataLines = lines.slice(2).join("\n");
    }
  }

  const jsonData = processCsvData(dataLines, collectionTimezone);
  const groupedData = groupBy(jsonData, "t");

  const rows = groupedData.map((group) => {
    const cleanedData = removeColumn(group.group, "t");
    const headers = Object.keys(cleanedData[0]);
    const csvContent = jsonToCsv(cleanedData, headers);
    const hash = sha3_256_hash_string(csvContent);
    return `${group.t},${hash},${hash}`;
  });

  const dataSection = `t,c,f\n${rows.join("\n")}\n`;

  // If metadata is absent (or empty), ship only the data section; backend will infer
  // collection metadata via SQL indexing service when configured.
  if (!collectionName && !userAddress) {
    return dataSection;
  }

  const metaHeader = "collection_name,user_address,collection_timezone\n";
  const metaRow = `${collectionName || ""},${userAddress || ""},${
    collectionTimezone || ""
  }\n`;
  return `${metaHeader}${metaRow}${dataSection}`;
};

export const processCsv = async (csvData, onErrorMessage) => {
  const finalCsv = processCSVWithHash(csvData, onErrorMessage);
  return finalCsv;
};
